/**
 * policyDraft — the insurance policy's own three values, and what a picker row says about a policy.
 *
 * THE JUNCTION IS THE GATE; THESE ARE THE RECORD. `sign_contract` reads exactly one thing in the
 * insurance area — whether an `insurance_contracts` row exists — and the hire purchase agreement's
 * eighteen merge fields contain zero insurance fields. The insured value, the cover period and the
 * certificate are advisory, and the copy on this canvas says so. What they are NOT is optional to
 * get right: an expired policy on a car being handed over is a real exposure, which is why the
 * period is rendered as a period rather than as two dates and why the picker rows carry it.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only.
 */

import { addDays, addYears, differenceInCalendarDays, format } from 'date-fns';

import { coverExpiryStatus, daysToExpiry } from '@/lib/coverExpiry';
import { businessDay, formatCurrency } from '@/lib/format';

/** The fields the policy group holds, as strings, exactly as the inputs hold them. */
export interface PolicyDraft {
    valuedAt: string;
    startsAt: string;
    expiresAt: string;
}

/**
 * The refusal for a policy that cannot be saved, or null.
 *
 * THE SENTENCES ARE THE ONES THE SHEET ALREADY RAISED, kept word for word: they were already the
 * right sentences, and rewriting a refusal an admin has learned to recognise is churn.
 *
 * AND THERE ARE THREE OF THEM, WHICH IS ALSO THE SHEET'S COUNT. A fourth rule refusing a period
 * that ends before it begins was added here and then taken out again: this validator gates EVERY
 * save of the group, not just the dates, so a legacy policy already carrying backwards dates would
 * have had its insured value and its certificate locked behind a correction of somebody else's
 * typo. The expiry is advisory — `sign_contract` reads only whether the junction row exists — and
 * `policyExpiryNote` below already says out loud what a bad period means. Parity with the sheet is
 * the claim this docblock makes, so it is the rule the code keeps.
 */
export function validatePolicyDraft(draft: PolicyDraft): string | null {
    const valued = parseFloat(draft.valuedAt);
    if (isNaN(valued) || valued <= 0) return 'Enter a valid insured value';
    if (!draft.startsAt) return 'Select a start date';
    if (!draft.expiresAt) return 'Select an expiry date';
    return null;
}

/**
 * `Jan 4, 2026 — Jan 3, 2027`, or null when the policy carries no readable period.
 *
 * THE PERIOD IS A PAIR OF CALENDAR DAYS, NOT A PAIR OF INSTANTS. Both ends arrive either as the
 * date input's own `yyyy-MM-dd` or as `insurances.starts_at`/`expires_at` — TIMESTAMPTZ columns the
 * form fills from that same input, so Accra midnight. `new Date(…)` on either shape yields an
 * instant that date-fns then renders in the VIEWER's zone, which west of Greenwich is the day
 * before: `Jan 3, 2026` over a policy that incepts on the 4th, on a screen whose whole point is
 * whether the cover is live. `businessDay` lands both shapes on the Ghana calendar day.
 */
export function policyPeriodText(
    startsAt: string | null | undefined,
    expiresAt: string | null | undefined,
): string | null {
    const from = businessDay(startsAt);
    const to = businessDay(expiresAt);
    if (!from || !to) return null;
    return `${format(from, 'MMM d, yyyy')} — ${format(to, 'MMM d, yyyy')}`;
}

/**
 * `Expired` / `18d left` / `On cover` — how urgent this policy's clock is.
 *
 * EXPIRY IS THE ONE THING A POLICY ROW CAN BE WRONG ABOUT SILENTLY. A junction row against an
 * expired policy still clears the gate, so the row has to say what the gate will not.
 *
 * COUNTED IN THE SAME CALENDAR THE PERIOD IS PRINTED IN. `policyPeriodText` above lands both ends
 * on the Ghana day; counting the days left off `new Date(expiresAt)` against the HOST zone's today
 * mixed two calendars inside one rendered string — `policyMeta` joins them, so the row could read
 * `Jan 4, 2026 — Aug 21, 2026 · 17d left` over a period whose own dates are 18 days apart. Both
 * halves are business days now, and the count is derived from the injected `now` rather than from
 * `businessToday()` so a caller (and every test) keeps its clock.
 *
 * THE BUCKETS ARE THE SHARED RULE (G6, `coverExpiryStatus`): good through the expiry date on the
 * Ghana day, within 30 days inclusive — the rule this note already kept, now kept in one place.
 */
export function policyExpiryNote(
    expiresAt: string | null | undefined,
    now: Date = new Date(),
): string | null {
    const status = coverExpiryStatus(expiresAt, now);
    const days = daysToExpiry(expiresAt, now);
    if (status === 'expired') return 'expired';
    if (status !== 'expiring' || days === null) return null;
    return days === 0 ? 'expires today' : `${days}d left`;
}

/**
 * How a cover period sits against its own anniversary.
 *
 * CALENDAR ARITHMETIC, NOT A DAY COUNT, and production is why. Of 353 policies linked to a
 * contract, 343 run to the day BEFORE the anniversary and one runs to the anniversary itself —
 * both are ordinary years, and both have to stay silent. A day count cannot hold that: 45 of the
 * day-before years measure 365 days because they span a Feb 29, and a same-day year that spans one
 * measures 366 — the same number as the three policies that are genuinely one day long. `addYears`
 * clamps Feb 29 to Feb 28, so a leap-day inception lands correctly too.
 *
 * THE WINDOW IS TWO DAYS WIDE AND THAT IS DELIBERATE. Wide enough to cover both conventions a
 * certificate may be written in, narrow enough that a one-day slip from either one still lands
 * outside — which is exactly what the three 366-day policies are.
 */
type PeriodShape =
    | { kind: 'unreadable' }
    | { kind: 'annual' }
    | { kind: 'backwards' }
    | { kind: 'sameDay' }
    | { kind: 'off'; days: number };

function periodShape(
    startsAt: string | null | undefined,
    expiresAt: string | null | undefined,
): PeriodShape {
    const from = businessDay(startsAt);
    const to = businessDay(expiresAt);
    if (!from || !to) return { kind: 'unreadable' };
    const days = differenceInCalendarDays(to, from);
    if (days < 0) return { kind: 'backwards' };
    if (days === 0) return { kind: 'sameDay' };
    const anniversary = addYears(from, 1);
    const eve = addDays(anniversary, -1);
    const annual =
        differenceInCalendarDays(to, eve) >= 0 && differenceInCalendarDays(to, anniversary) <= 0;
    return annual ? { kind: 'annual' } : { kind: 'off', days };
}

/**
 * The sentence a period that is not a year earns, or null.
 *
 * ADVISORY, LIKE EVERYTHING ELSE ABOUT THIS PERIOD. `validatePolicyDraft` above keeps the sheet's
 * three refusals and no more, for the reason its docblock gives; this is what says out loud what
 * the gate will not. It never names a corrected date: the dates have to equal the CERTIFICATE's
 * period, and offering an arithmetic answer invites an admin to take it instead of reading the
 * document.
 */
export function policyPeriodWarning(
    startsAt: string | null | undefined,
    expiresAt: string | null | undefined,
): string | null {
    const shape = periodShape(startsAt, expiresAt);
    switch (shape.kind) {
        case 'backwards':
            return 'Cover expires before it starts. Check the certificate.';
        case 'sameDay':
            return 'Cover starts and expires on the same day. Check the certificate.';
        case 'off':
            return `Cover runs ${shape.days} ${shape.days === 1 ? 'day' : 'days'}, not a year. Check the certificate.`;
        default:
            return null;
    }
}

/**
 * The same fact as a summary tail — `· not a year`, in the rhythm `policyMeta` already uses.
 *
 * THE HEAD IS WHERE THIS HAS TO REACH. The sentence below the fields only finds an admin who
 * opened the group; nine live policies are already wrong and nobody is opening those.
 */
export function policyPeriodFlag(
    startsAt: string | null | undefined,
    expiresAt: string | null | undefined,
): string | null {
    const shape = periodShape(startsAt, expiresAt);
    if (shape.kind === 'backwards') return 'expires before it starts';
    if (shape.kind === 'sameDay' || shape.kind === 'off') return 'not a year';
    return null;
}

/** A policy as a picker row reads it. `get_reusable_insurances` returns exactly these columns. */
export interface PickablePolicy {
    id: number;
    valued_at: number | null;
    starts_at: string | null;
    expires_at: string | null;
    certificate_url?: string | null;
}

/** `Insured at GH₵50,000.00` — or the policy's id when it carries no value. */
export function policyLabel(policy: PickablePolicy): string {
    return policy.valued_at == null
        ? `Policy #${policy.id}`
        : `Insured at ${formatCurrency(policy.valued_at)}`;
}

/** `Jan 4, 2026 — Jan 3, 2027 · 18d left`. */
export function policyMeta(policy: PickablePolicy, now: Date = new Date()): string {
    return [policyPeriodText(policy.starts_at, policy.expires_at) ?? 'no period recorded',
        policyExpiryNote(policy.expires_at, now)]
        .filter(Boolean)
        .join(' · ');
}

/**
 * Value, period and id — everything a policy row answers to.
 *
 * The dates are the BUSINESS days, for the reason `policyPeriodText` gives, and here it is also a
 * matching rule: a row that displays `Jan 4, 2026` has to be findable by typing it.
 */
export function policySearchText(policy: PickablePolicy): string {
    const from = businessDay(policy.starts_at);
    const to = businessDay(policy.expires_at);
    return [
        String(policy.valued_at ?? ''),
        from ? format(from, 'MMM d yyyy') : '',
        to ? format(to, 'MMM d yyyy') : '',
        `#${policy.id}`,
    ]
        .filter(Boolean)
        .join(' ');
}

/**
 * The policies this row may pick from, with whatever is linked restored to the top.
 *
 * `get_reusable_insurances` EXCLUDES THIS CONTRACT'S OWN POLICY (that is what its
 * `p_exclude_contract_id` argument does), so without this the trigger names a policy the open
 * list denies exists — and the tick that says "this is the one on cover" never appears.
 */
export function withCurrentPolicy(
    pool: PickablePolicy[] | undefined,
    current: PickablePolicy | null | undefined,
): PickablePolicy[] {
    const rows = [...(pool ?? [])];
    if (current && !rows.some((row) => row.id === current.id)) rows.unshift(current);
    return rows;
}
